/**
 * https://developers.surfboardpayments.com/api/orders?lang=Node.js#Create-New-Order
 */

export interface SurfboardOrder {
  // Id of the checkout / payment terminal
  terminal$id: string;
  // Purchase Order Id is mandatory if type is 'return'
  purchaseOrderId?: string;
  // This specifies whether the purchase was made for a company or not
  companyPurchase?: boolean;
  // If the purchase was a company purchase specify company details here
  company?: SurfboardCompanyDetails;
  // Add customer details here
  customer?: SurfboardOrderCustomerInformation;

  // Type of the order, it can be either 'purchase' or 'return'
  type: 'purchase' | 'return';
  // Date of the order created
  date?: string;

  // ID of the external order created
  referenceId: string;

  // List of order lines
  orderLines: SurfboardOrderLine[];

  // Total amount details of the order.
  totalOrderAmount?: SurfboardTotalOrderAmountDetails;

  // Any additional information that you want to tag to the order can be added here as a key value pairs.
  metadata?: any;
  adjustments?: SurfboardAdjustment[];
  controlFunctions?: SurfboardControlFunctions;
}

export interface SurfboardRefundMetadata {

  source: string;
  operation: string; // 'refund'
  refundData: SufboardRefundMetadataRefundData;
}

export interface SufboardRefundMetadataRefundData {
  // the id of the payment for which the refund is being done (backup to referenceId because it is not clearly mentioned in the documentation -> so we don't have surprises if it is stripped at some moment)
  paymentId: number,
  // the digital giftcardId for which the refund is being done
  giftcardId?: number;
  // the physicalGiftcard Id for which the refund is being done
  physicalGiftcardId?: number;
  // pass down the giftcardReasonId the user has selected for refund
  giftcardReasonId?: number,
  // pass down the note the user has entered for refund
  refundNote?: string,
  // the id of the user performing the refund
  lastModifiedUserId: number,
}

// This is the required data from the Order when creating a refund
export interface SurfboardRefundOrder {
  // Id of the checkout / payment terminal
  terminal$id: string;
  // Type of the order, it can only be 'return' for refunds
  type: 'return';
  // Purchase Order Id is mandatory if type is 'return'
  purchaseOrderId: string;

  // ID of the external order created
  referenceId: string;

  // This specifies whether the purchase was made for a company or not
  companyPurchase?: boolean;
  // If the purchase was a company purchase specify company details here
  company?: SurfboardCompanyDetails;

  // Add customer details here
  customer?: SurfboardOrderCustomerInformation;

  // documentation: https://lithium.developers.surfboardpayments.com/guides/in-store-payments/post-payments/return-refund/return-refund#partial-refunds
  // List of order lines (required only for partial refunds)
  orderLines?: SurfboardRefundOrderItem[];

  // documentation: https://lithium.developers.surfboardpayments.com/guides/in-store-payments/post-payments/return-refund/return-refund#partial-refunds
  // there is a possibility to refund by using the totalOrderAmount section
  totalOrderAmount?: any

  metadata?: SurfboardRefundMetadata;
  controlFunctions?: SurfboardControlFunctions;
}

export interface SurfboardOrderCustomerInformation {
  person?: {
    // id of the customer
    id: string;
    name?: {
      // First name of the customer
      firstName?: string;
      // Middle name of the customer
      middleName?: string;
      // Last name of the customer
      lastName?: string;
    }
    // Email Id of the customer
    email?: string;
    // Mobile number of the customer
    phoneNumber: SurfboardPhoneNumber;
    // DOB of the customer
    birthDate?: string;
    // Social security number of the customer
    personalNumber?: string;
    // Coordination number of the customer
    coordinationNumber?: string;
    // Residence country of the customer
    countryResidence?: string;
  }
  billing?: {
    // Name of the addressee that is accepting the correspondence for the intended recipient.
    careOf?: string;
    address1: string;
    address2?: string;
    address3?: string;
    city: string;
    postalCode: string;
    country: string;
  }
  shipping?: {
    name: {
      // First name of the customer
      firstName: string;
      // Middle name of the customer
      middleName?: string;
      // Last name of the customer
      lastName: string;
    }
    // The email address of the recipient.
    email: string;
    // Mobile number of the customer
    phoneNumber: SurfboardPhoneNumber;
    // The name of the person or entity who will receive the shipment if different from the recipient.
    careOf?: string;
    address1: string;
    address2?: string;
    address3?: string;
    city: string;
    postalCode: string;
    country: string;
  }
}

export interface SurfboardOrderLine {
  id: string;
  name: string;
  quantity: number;
  imageUrl?: string;
  itemAmount: {
    // Regular price of the product in the smallest currency unit (eg.,10 SEK is 1000 in amount)
    regular: number;
    // Discount amount in the smallest currency unit (eg.,10 SEK is 1000 in amount)
    campaign?: number;
    // Shipping amount of the item in the smallest currency unit (eg.,10 SEK is 1000 in amount)
    shipping?: number;
    // Total amount payable by the customer per unit in smallest currency units (eg.,10 SEK is 1000 in amount). Total amount is calculated as (regular - campaign) +  shipping. If orderLineLevelCalculation is set to true, the total will be calculated as total = ((regular * quantity) - campaign) + shipping.
    total: number;
    // Type of currency in ISO 4217 format (e.g., 'SEK' for Swedish Krona, 'EUR' for Euro, 'DKK' for Danish krone).
    currency: string;
    tax: {
      // Taxable amount in the smallest currency unit (eg.,10 SEK is 1000 in amount)
      amount: number;
      // Percentage of respective tax
      percentage: number;
      // Type of the tax supported currently is 'vat'
      type: string;
    }[]
  }
}

export interface SurfboardTotalOrderAmountDetails {
  // Regular price of the order in the smallest currency unit (eg.,10 SEK is 1000 in amount)
  regular: number;
  // Discount amount in the smallest currency unit (eg.,10 SEK is 1000 in amount)
  campaign?: number;
  // Shipping amount of the order in the smallest currency unit (eg.,10 SEK is 1000 in amount)
  shipping?: number;
  // Actual amount that the customer has to pay in the smallest currency unit (eg.,10 SEK is 1000 in amount). Total amount is calculated as (regular - campaign) + shipping + adjustments.
  total: number;
  // Type of currency in ISO 4217 format (e.g., 'SEK' for Swedish Krona, 'EUR' for Euro, 'DKK' for Danish krone).
  currency: string;
  // Array of taxes for the item/order
  tax: SurfboardTax[];
}

export interface SurfboardPhoneNumber {
  // International dialing code identifying the country or region.
  code: string;
  // A string of numbers ranging from 0-9 with a length of 5-15 characters.
  number: string
}

export interface SurfboardCompanyDetails {
  // Id of the company purchased for
  id: string;
  // Vat Id of the company purchased for
  vatId?: string;
  // PO Details of the company purchased for
  poDetails: string;
}

export interface SurfboardTax {
  // Taxable amount in the smallest currency unit (eg.,10 SEK is 1000 in amount)
  amount: number;
  // Percentage of respective tax
  percentage: number;
  // Type of the tax supported currently is 'vat'
  type: string;
}

export interface SurfboardAdjustment {
  type: string;
  value: string;
  // Metadata associated with the adjustment in key value pairs.
  metaData?: any;
}

export interface SurfboardControlFunctions {
  // Set as true when you want to cancel all previous pending orders
  cancelPreviousPendingOrder?: boolean;
  // Set this to true to apply campaign and shipping at row level. The total will be calculated as total = ((regular * quantity) - campaign) + shipping. Default is set to false.
  orderLineLevelCalculation?: boolean;
  // Customize the text displayed on the payment page button to align with your specific needs. Possible values are 'ADD_MONEY' , 'BOOK' , 'BUY' , 'CHECKOUT' , 'CONTINUE' , 'DONATE' , 'ORDER' , 'PAY' , 'RENT' , 'SUBSCRIBE' , 'SUPPORT' , 'TIP' , 'TOP_UP'.
  payButtonType?: string;
  // If true, requires the customer to tap the card before the amount is entered, ensuring amount confirmation prior to payment.
  tapBeforeAmount?: boolean;
  // Applies for return orders on whether to include the adjustments as part of the refund or not. The default value is true. For partial returns, by default the first order will have includeAdjustments set to true and for the rest as false.
  includeAdjustments?: boolean;
  // An array of payment methods locked in. All available payment methods are supported.
  lockToPaymentMethods?: string[];
  // Allows capture at a later date.
  delayCapture?: boolean;
  // Delays the final amount paid out to the merchant after all deductions, in the format numbers + 'm' | 'h' | 'd' .
  delayPayout?: string;
  // This is a dynamic callback URL that is used to perform webhook calls in relation to the order
  callBackUrl?: string;
  // Throws an error if the respective terminal has been inactive for the given time frame(Time in seconds).
  throwErrorIfTerminalInactiveFor?: number;
  // It can be either PRE-AUTH or AUTH. By default it is set to AUTH. If PRE-AUTH is selected delayCapture is automatically set to true.
  authMode?: string;

  // region -- COMING SOON
  // Indicates whether a receipt should be printed for the transaction. If true, the terminal will print a receipt after payment.
  printReceipt?: boolean;

  surcharges?: SurfboardSurcharges[];

  // A date field in yyyy-MM-dd format indicating when fulfillment occurs.
  fulfillmentDate?: string;
  // endregion coming soon

  // This field denotes the type of NFC reading allowed on the terminal for the order.
  readTags?: string;

  online?: SurfboardOnlineDetails;
  recurring?: SurfboardRecurringDetails;
}

export interface SurfboardSurcharges {
  domestic?: {
    consumer?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
    }
    corporate?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }

    }
  }
  international?: {
    consumer?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
    }
    corporate?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }

    }
  }
  eea?: {
    consumer?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
    }
    corporate?: {
      debit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }
      credit?: {
        fixedAmount?: number;
        fixedPercentage?: number;
      }

    }
  }
}

export interface SurfboardOnlineDetails {
  enforceTokenization?: boolean;
  // Denotes default validity of the payment page, in the format numbers + 'm' | 'h' | 'd' .
  paymentPageValidFor?: string;
  // This will be the redirect URL from the payment page for online transactions once the transaction is processed. The orderId will be included as a query param in the redirectUrl.
  redirectUrl?: string;
  // The payment page will be redirected to this URL in case of failed transactions if provided. The orderId will be included as a query param to the redirected url.
  failureRedirectUrl?: string;
  // Set as true when you need the short URL for the payment page. Default is set to false.
  generateShortLink?: boolean;
}

export interface SurfboardRecurringDetails {
  // Determines the type of the subscription amount for order. The possible values are, 'fixed' (A fixed amount is collected.) or 'variable' (A variable amount is collected.)
  subscriptionAmountType?: string;
  // Maximum amount for the order in lowest currency unit (only valid for variable subscription amounts)
  maxAmount?: number;
  // Frequency of the recurring payment collection. Possible values are 'daily' | 'twiceWeekly' | 'weekly' | 'tenDays' | 'fortNightly' | 'monthly' | 'everyTwoMonths' | 'trimester' | 'quarterly' | 'twiceYearly' | 'annually' | 'unscheduled'. Mandatory parameter for Subscription based orders.
  frequency?: string;
  // Total no of payments that is expected to be executed for this order.
  numberOfPayments?: string;
  // A unique reference for the recurring order.
  uniqueReference?: string;
}

/**
 * Represents the detailed financial breakdown for a refunded item.
 */
interface SurfboardRefundItemAmount {
  /** The regular price of the item. */
  regular: number;
  /** The total price of the item after all calculations. */
  total: number;
  /** The amount discounted due to a campaign or promotion (should be 0 for a full refund of an original item amount). */
  campaign: number;
  /** The shipping cost associated with the item (often 0 for single items). */
  shipping: number;
  /** The currency code (e.g., "EUR"). */
  currency: 'EUR' | string;
  /** An array to hold tax details. */
  tax: SurfboardTax[];
}

/**
 * Represents a single item within a refund transaction.
 */
interface SurfboardRefundOrderItem {
  /** A unique identifier for the item. */
  id: string;
  /** A descriptive name for the item. */
  name: string;
  /** The number of units of this item being refunded. */
  quantity: number;
  /** Detailed breakdown of the item's pricing. */
  itemAmount: SurfboardRefundItemAmount;
}

export interface SurfboardOrderPaymentData {
  aId: string;
  dccRate: number | null;
  orderId: string;
  authCode: string;
  cardType: string;
  dccAmount: number | null;
  dccMarkup: number | null;
  entryMode: string;
  uniqueRef: string;
  cardNumber: string;
  merchantId: string;
  terminalId: string;
  dccCurrency: string | null;
  isAibPayment?: boolean;
  dccDisclaimer?: string | null;
}
